import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { BackIcon } from '../components/icons.js';
import { Frame, MEMORY_KIND_LABELS, MemoryKind, type Conversation, type MemoryItem } from '@jabberr/core';
import { EmptyState } from '../components/EmptyState.js';
import { ErrorBox, Empty, Spinner } from '../components/ui.js';
import { api, errorMessage } from '../lib/api.js';
import { conversationTitle, relativeTime } from '../lib/format.js';
import { useDrawer } from '../state/drawer.js';
import { useSession } from '../state/session.js';

const PROJECT_KINDS = MemoryKind.options.filter((k) => k !== 'preference');

/** What Jabberr carries into new conversations. Everything here can be corrected or retired by a person. */
export function MemoryPage() {
  const { projectId = '' } = useParams();
  // setInfo alone: the drawer object changes whenever its info does, so depending on it re-ran this effect forever.
  const { setInfo } = useDrawer();
  const { user } = useSession();
  const [project, setProject] = useState<MemoryItem[] | null>(null);
  const [mine, setMine] = useState<MemoryItem[]>([]);
  const [showRetired, setShowRetired] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.memory.list(projectId);
      setProject(res.project);
      setMine(res.user);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [projectId]);

  useEffect(() => {
    setProject(null);
    void load();
    setInfo({ projectId, title: 'What Jabberr remembers' });
    // Visiting the page marks its memory as seen, so a later reply mentions only truly new items.
    try {
      localStorage.setItem(`jabberr:memorySeen:${projectId}`, new Date().toISOString());
    } catch {
      // Still works without storage.
    }
  }, [load, projectId, setInfo]);

  const visible = (items: MemoryItem[]) => items.filter((i) => showRetired || i.status !== 'retired');

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <Link to={`/p/${projectId}/knows`} className="back-link">
          <BackIcon />
          What Jabberr knows
        </Link>
          <h1>What Jabberr remembers</h1>
        </div>
        <label className="small muted row">
          <input type="checkbox" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} /> show forgotten
        </label>
      </header>
      <p className="muted">
        What Jabberr picked up in earlier conversations and meetings. A new conversation starts with these notes; one already under way keeps its starting notes and can recall relevant updates. Fix a note that is wrong and forget one that is no longer true. The project notes still outrank anything here.
      </p>
      {error ? <ErrorBox>{error}</ErrorBox> : null}
      <ExtractNow projectId={projectId} onError={setError} onAdded={() => void load()} />
      <AddMemory projectId={projectId} onAdded={() => void load()} onError={setError} />

      {project === null ? (
        <Spinner label="Loading" />
      ) : (
        <>
          {PROJECT_KINDS.map((kind) => {
            const items = visible(project.filter((i) => i.kind === kind));
            return (
              <section key={kind}>
                <div className="section-head">
                  <h2>{MEMORY_KIND_LABELS[kind]}</h2>
                </div>
                {items.length === 0 ? (
                  <EmptyState title="Nothing yet." />
                ) : (
                  <ul className="list compact">{items.map((i) => <MemoryRow key={i.id} item={i} previousText={[...(project ?? []), ...mine].find(row => row.id === i.replacesMemoryId)?.text} onChange={() => void load()} onError={setError} />)}</ul>
                )}
              </section>
            );
          })}
          <section>
            <div className="section-head">
              <h2>About {user?.name ?? 'you'}</h2>
            </div>
            <p className="muted small">Only you see this, in every project. Nobody else's conversations carry it.</p>
            {visible(mine).length === 0 ? (
              <EmptyState title="Nothing yet." />
            ) : (
              <ul className="list compact">{visible(mine).map((i) => <MemoryRow key={i.id} item={i} previousText={[...(project ?? []), ...mine].find(row => row.id === i.replacesMemoryId)?.text} onChange={() => void load()} onError={setError} />)}</ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function MemoryRow({ item, previousText, onChange, onError }: { item: MemoryItem; previousText?: string; onChange: (i: MemoryItem) => void; onError: (m: string) => void }) {
  const { projectId = '' } = useParams();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(item.text);

  const patch = async (body: Parameters<typeof api.memory.update>[1]): Promise<void> => {
    try {
      onChange((await api.memory.update(item.id, body)).item);
      setEditing(false);
    } catch (err) {
      onError(errorMessage(err));
    }
  };

  return (
    <li id={`memory-${item.id}`} className={`list-item memory-item ${item.status === 'retired' ? 'retired' : ''}`}>
      {editing ? (
        <form
          className="row wrap grow"
          onSubmit={(e) => {
            e.preventDefault();
            void patch({ text: text.trim() });
          }}
        >
          <input className="grow" value={text} maxLength={500} onChange={(e) => setText(e.target.value)} aria-label="Memory text" autoFocus />
          <button type="submit" className="btn" disabled={!text.trim()}>
            Save
          </button>
          <button type="button" className="link-btn" onClick={() => setEditing(false)}>
            cancel
          </button>
        </form>
      ) : (
        <>
          <div className="grow memory-text">
            {item.text}{' '}
            <span className="muted tiny">
              {item.source === 'manual' ? 'added by hand' : 'from a conversation'} · {relativeTime(item.createdAt)}
              {item.frame !== 'general' ? ` · about ${item.frame}` : ''}
            </span>{' '}
            {item.status !== 'active' ? <span className={`chip ${item.status === 'proposed' ? 'warn' : ''}`}>{item.status === 'proposed' ? 'not confirmed' : 'forgotten'}</span> : null}
          {item.status === 'proposed' && item.replacesMemoryId ? <p className="small"><a href={`#memory-${item.replacesMemoryId}`}>Review previous note: {previousText ?? 'open the original'}</a></p> : null}
          {item.sourceQuote ? <details className="small"><summary>Supporting statement</summary><blockquote>{item.sourceQuote}</blockquote></details> : null}
          {item.sourceConversationId ? <Link className="small" to={`/p/${item.sourceProjectId ?? item.projectId ?? projectId}/c/${item.sourceConversationId}${item.sourceTurnId ? `?highlight=${item.sourceTurnId}` : ''}`}>Open source</Link> : null}
          </div>
          <span className="row memory-actions">
            {item.status === 'proposed' ? <button type="button" className="link-btn small" onClick={() => void patch({ status: 'active' })}>
              {item.replacesMemoryId ? 'Confirm replacement' : 'Confirm note'}
            </button> : null}
            <button type="button" className="link-btn small" onClick={() => setEditing(true)} aria-label={`Edit ${item.text}`}>Edit</button>
            <button type="button" className="link-btn small" onClick={() => void patch({ status: item.status === 'retired' ? 'active' : 'retired' })} aria-label={`${item.status === 'retired' ? 'Bring back' : 'Forget'} ${item.text}`}>
              {item.status === 'retired' ? 'Bring back' : 'Forget'}
            </button>
          </span>
        </>
      )}
    </li>
  );
}

/** Pulls memory forward now instead of waiting for the automatic trigger, for a chosen thread. */
function ExtractNow({ projectId, onError, onAdded }: { projectId: string; onError: (m: string) => void; onAdded: () => void }) {
  const [conversations, setConversations] = useState<Conversation[] | null>(null);
  const [conversationId, setConversationId] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  useEffect(() => {
    api.conversations
      .list(projectId)
      .then((r) => setConversations(r.conversations.filter((c) => c.status === 'active' && c.lastTurnSeq > 0)))
      .catch(() => setConversations([]));
  }, [projectId]);


  return (
    <div className="memory-toolbar card">
      <div className="memory-toolbar-row">
        <label className="memory-field">
          <span>Look through a conversation</span>
          <select value={conversationId} onChange={(e) => setConversationId(e.target.value)} aria-label="Conversation" disabled={conversations === null}>
            <option value="">Pick a conversation...</option>
            {(conversations ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {conversationTitle(c.title)}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" className="btn" disabled={busy || !conversationId} onClick={() => {
          if (!conversationId || busy) return;
          setBusy(true);
          setResult(null);
          (async () => {
            try {
              const { ran, added, pending } = await api.memory.extract(conversationId);
              onAdded();
              setResult(pending ? `Remembered ${added.length}. More remains; try again to continue.` : !ran ? 'No notes saved. Nothing new or extraction could not complete; try again if needed.' : added.length === 0 ? 'Nothing worth keeping.' : `Remembered ${added.length}.`);
            } catch (err) {
              onError(errorMessage(err));
            } finally {
              setBusy(false);
            }
          })();
        }}>
          {busy ? 'Reading...' : 'Look for new notes now'}
        </button>
        {result ? (
          <span className="muted" role="status">
            {result}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function AddMemory({ projectId, onAdded, onError }: { projectId: string; onAdded: () => void; onError: (m: string) => void }) {
  const [kind, setKind] = useState<MemoryKind>('decision');
  const [frame, setFrame] = useState<Frame>('general');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.memory.create(projectId, { kind, text: text.trim(), frame });
      setText('');
      onAdded();
    } catch (err) {
      onError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="memory-add-row row wrap" onSubmit={(e) => void submit(e)}>
      <label className="memory-field">
        <span>Add a note</span>
        <select value={kind} onChange={(e) => setKind(e.target.value as MemoryKind)} aria-label="Kind">
          {MemoryKind.options.map((k) => (
            <option key={k} value={k}>
              {k === 'preference' ? 'About me' : MEMORY_KIND_LABELS[k]}
            </option>
          ))}
        </select>
      </label>
      <label className="memory-field">
        <span className="sr-only">About</span>
        <select value={frame} onChange={(e) => setFrame(e.target.value as Frame)} aria-label="About" title="Is this about what is released, what is in development, or neither?">
          <option value="general">general</option>
          <option value="production">about production</option>
          <option value="dev">about development</option>
        </select>
      </label>
      <input className="grow" value={text} maxLength={500} onChange={(e) => setText(e.target.value)} placeholder="One self-contained sentence worth carrying into future conversations" aria-label="Memory text" />
      <button type="submit" className="btn" disabled={busy || !text.trim()}>
        Add
      </button>
    </form>
  );
}
